import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { classes, Root } from './utils';
import closeIcon from '../../../../../assets/accountSetting-assets/x-circle.svg';
import { lightPalette } from '../../../../../theme';
import ConfirmDialog from '../../../utils/ConfirmDialog';
import {
  getLoggedInUserDetail,
  updateUserDetails,
} from '../../../../../services/userApi';
import { EDIT_DETAILS_TOAST } from '../../../../../constants/toastConstant';
import Toast from '../../../utils/Toast';
import { getLoggedInUser } from '../../../../../store/userSlice';
import {
  ACCOUNT_INFO,
  INVALID_INPUT_REGEX,
  getHelperText,
} from '../../../../../constants/staticContent';

const EditDetails = ({
  setEditDetailOpen,
  userDetail,
  editDetailOpen,
  isClosed,
  setIsClosed,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [avatar, setAvatar] = useState('');

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNo: false,
  });
  const [originalAvatarFile, setOriginalAvatarFile] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fileInputRef = useRef(null);

  const relevantFields = {
    first_name: userDetail.first_name,
    last_name: userDetail.last_name,
    email: userDetail.email,
    phone_no: userDetail.phone_no,
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogOpenConfirm, setIsDialogOpenConfirm] = useState(false);

  const validateName = (name) => {
    const nameRegex = INVALID_INPUT_REGEX.NAME;
    return nameRegex.test(name);
  };

  const validatePhone = (phone) => {
    const phoneRegex = INVALID_INPUT_REGEX.PHONE_NO;
    return phoneRegex.test(phone);
  };

  const handleOpenDialog = () => {
    if (
      originalAvatarFile ||
      JSON.stringify(formData) !== JSON.stringify(relevantFields)
    ) {
      setIsDialogOpen(true);
    } else {
      setEditDetailOpen(false);
    }
  };
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleOpenConfirmDialog = () => {
    if (
      originalAvatarFile ||
      JSON.stringify(formData) !== JSON.stringify(relevantFields)
    ) {
      const fnameValid = validateName(formData.first_name);
      const lnameValid = validateName(formData.last_name);
      const phoneValid = validatePhone(formData.phone_no);

      setErrors({
        firstName: !fnameValid,
        lastName: !lnameValid,
        phoneNo: !phoneValid,
      });
      if (fnameValid && lnameValid && phoneValid) {
        setIsDialogOpenConfirm(true);
      }
    } else {
      Toast({
        message: ACCOUNT_INFO.BILLING_INFO.NO_UPDATE_MESSAGE,
        variant: 'warning',
      });
    }
  };
  const handleCloseConfirmDialog = () => {
    setIsDialogOpenConfirm(false);
  };

  const CloseEditInfo = () => {
    setEditDetailOpen(false);
    setIsClosed(false);
    setIsDialogOpen(false);
    setErrors({});
    setFormData({});
    setAvatar('');
    setOriginalAvatarFile('');
  };

  const handleDiscardChangesConfirmAction = async () => {
    handleCloseDialog();
    setEditDetailOpen(false);
    setIsClosed(false);
    setErrors({});
    setFormData({});
    setAvatar('');
    setOriginalAvatarFile('');
  };

  const handleButtonClick = () => {
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const handleAvatarUpload = (e) => {
    const allowedFileTypesForAvatar = ['image/jpeg', 'image/jpg', 'image/png'];
    const file = e.target.files[0];
    if (file?.name?.includes(' ')) {
      Toast({
        message: EDIT_DETAILS_TOAST.SPACES_NOT_ALLOWED,
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
      });
    } else if (file && allowedFileTypesForAvatar.includes(file.type)) {
      setOriginalAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataURL = reader.result;
        setAvatar(dataURL);
      };
      reader.readAsDataURL(file);
    } else {
      Toast({
        message: EDIT_DETAILS_TOAST.FORMAT_INVALID,
        variant: 'error',
        position: { vertical: 'top', horizontal: 'center' },
      });
    }
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    const ValidateValue =
      name === 'phone_no' ? value.replace(/\D/g, '') : value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: ValidateValue,
    }));
    if (name === 'first_name' || name === 'last_name' || name === 'phone_no') {
      const isValidFname = validateName(value);
      const isValidLname = validateName(value);
      const isValidPhone = validatePhone(ValidateValue);
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstName: name === 'first_name' ? !isValidFname : prevErrors.firstName,
        lastName: name === 'last_name' ? !isValidLname : prevErrors.lastName,
        phoneNo: name === 'phone_no' ? !isValidPhone : prevErrors.phoneNo,
      }));
    }
  };
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fnameValid = validateName(formData.first_name);
    const lnameValid = validateName(formData.last_name);
    const phoneValid = validatePhone(formData.phone_no);

    setErrors({
      firstName: !fnameValid,
      lastName: !lnameValid,
      phoneNo: !phoneValid,
    });

    const formDataUserDetails = new FormData();
    if (fnameValid && lnameValid && phoneValid) {
      const isFormDataChanged =
        originalAvatarFile ||
        JSON.stringify(formData) !== JSON.stringify(relevantFields);

      if (originalAvatarFile) {
        formDataUserDetails.append('avatar', originalAvatarFile);
      }
      if (JSON.stringify(formData) !== JSON.stringify(relevantFields)) {
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== relevantFields[key]) {
            formDataUserDetails.append(key, value);
          }
        });
      }

      if (isFormDataChanged) {
        setIsDialogOpenConfirm(false);

        try {
          setIsLoading(true);
          const response = await updateUserDetails({
            config,
            formDataUserDetails,
          });
          if (response && response.status === 200) {
            setIsLoading(false);
            const loggedInUser = await getLoggedInUserDetail();
            dispatch(getLoggedInUser(loggedInUser));

            handleCloseConfirmDialog();
            setEditDetailOpen(false);
            setIsClosed(false);
            setErrors({});
            setFormData({});
            setAvatar('');
            setOriginalAvatarFile('');
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          setIsLoading(false);
          return error;
        }
      }
    }
  };

  useEffect(() => {
    if (editDetailOpen && !isClosed) {
      setFormData({
        first_name: userDetail?.first_name || '',
        last_name: userDetail?.last_name || '',
        email: userDetail?.email || '',
        phone_no: userDetail?.phone_no || '',
      });
      setAvatar(userDetail?.avatar || '');
    }
  }, [editDetailOpen]);

  const hasAnyError = Object.values(errors).some((value) => value === true);

  return (
    <Root className={classes.root}>
      <Card
        className={
          hasAnyError ? classes.errorCardComponent : classes.CardComponent
        }
      >
        <CardHeader
          className={classes.CardHeader}
          title={
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <Typography variant="h4" className={classes.HeaderTypography}>
                  {ACCOUNT_INFO.BILLING_INFO.EDIT_DETAILS}
                </Typography>
              </Grid>
              <Grid item xs={2} align="right">
                <Avatar
                  onClick={CloseEditInfo}
                  src={closeIcon}
                  className={classes.CloseAction}
                />
              </Grid>
            </Grid>
          }
        />
        {isLoading ? (
          <Box className={classes.loaderBox}>
            <CircularProgress />
          </Box>
        ) : (
          <CardContent className={classes.CardContent}>
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <FormControl fullWidth>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="body1"
                          className={classes.GridItemsTypography}
                        >
                          First Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          name="first_name"
                          className={classes.GridItemTextfield}
                          onChange={handleOnChange}
                          value={formData.first_name}
                          error={errors.firstName}
                          helperText={getHelperText(
                            'firstName',
                            'first_name',
                            errors,
                            formData,
                          )}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="body1"
                          className={classes.GridItemsTypography}
                        >
                          Last Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          name="last_name"
                          className={classes.GridItemTextfield}
                          onChange={handleOnChange}
                          value={formData.last_name}
                          error={errors.lastName}
                          helperText={getHelperText(
                            'lastName',
                            'last_name',
                            errors,
                            formData,
                          )}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={5.85}>
                      <FormControl fullWidth>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="body1"
                            className={classes.GridItemsTypography}
                          >
                            Email
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="email"
                            disabled
                            autoComplete="email"
                            className={classes.GridItemTextfield}
                            value={formData.email}
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="body1"
                          className={classes.GridItemsTypography}
                        >
                          Phone No.
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          name="phone_no"
                          className={classes.GridItemTextfield}
                          onChange={handleOnChange}
                          value={formData.phone_no}
                          error={errors.phoneNo}
                          helperText={getHelperText(
                            'phoneNo',
                            'phone_no',
                            errors,
                            formData,
                          )}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.GridBox}>
                  <Box>
                    <Typography variant="body1">Avatar</Typography>
                    <Typography
                      variant="subtitle1"
                      color={lightPalette.shoeWax.darker}
                    >
                      {ACCOUNT_INFO.BILLING_INFO.AVATAR_MESSAGE}
                    </Typography>
                    <Box className={classes.AvatarBox}>
                      <Avatar className={classes.avatar} src={avatar} />
                      <Box
                        className={classes.AvatarButtonContainer}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <input
                          type="file"
                          name="avatar"
                          className="upload-input"
                          ref={fileInputRef}
                          accept=".jpg, .jpeg, .png"
                          onChange={handleAvatarUpload}
                        />
                        <Button
                          className={classes.uploadAvatarButton}
                          variant="contained"
                          onClick={handleButtonClick}
                        >
                          <Typography
                            variant="body1"
                            sx={{ color: lightPalette.darkPurple.main }}
                          >
                            Upload Avatar
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </FormControl>
            </Box>
            <Box className={classes.Box}>
              <Button
                className={classes.CancelButton}
                onClick={handleOpenDialog}
              >
                <Typography variant="body1">
                  {ACCOUNT_INFO.BILLING_INFO.CANCEL_BUTTON}
                </Typography>
              </Button>
              <Button
                variant="contained"
                className={classes.UpdateButton}
                onClick={handleOpenConfirmDialog}
              >
                <Typography variant="body1">
                  {ACCOUNT_INFO.BILLING_INFO.UPDATE_BUTTON}
                </Typography>
              </Button>
            </Box>
          </CardContent>
        )}
      </Card>
      {isDialogOpen ? (
        <ConfirmDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleDiscardChangesConfirmAction}
          askingMessage={
            ACCOUNT_INFO.EDIT_DETAILS.ASKING_MESSAGE_OF_DISCARD_CHANGES
          }
        />
      ) : null}

      {isDialogOpenConfirm ? (
        <ConfirmDialog
          open={isDialogOpenConfirm}
          onClose={handleCloseConfirmDialog}
          onConfirm={handleSubmit}
          askingMessage={
            ACCOUNT_INFO.EDIT_DETAILS.ASKING_MESSAGE_OF_UPDATE_DETAILS
          }
        />
      ) : null}
    </Root>
  );
};
EditDetails.propTypes = {
  setEditDetailOpen: PropTypes.func.isRequired,
  userDetail: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone_no: PropTypes.string.isRequired,
    avatar: PropTypes.string.isRequired,
  }).isRequired,
  editDetailOpen: PropTypes.bool.isRequired,
  isClosed: PropTypes.bool.isRequired,
  setIsClosed: PropTypes.func.isRequired,
};

export default EditDetails;
